<template>
  <div>
    <HeadingExtraSmall
      element="h3"
      :hasMargin="false"
    >
      Resource
    </HeadingExtraSmall>
    <div
      :class="[$style.fieldGroup, $style.assignmentResource]"
      :data-test-id="$testId('assignmentResource')"
    >
      <div :class="$style.iconWrapper">
        <Icon
          :icon="assignmentData.icon"
          inline
          :scale="1.5"
        />
      </div>
      <HeadingExtraSmall
        :class="$style.assignmentIconTitle"
        element="h4"
      >
        {{ assignmentData.text }}
      </HeadingExtraSmall>
    </div>
    <div
      v-if="isSSO"
      :class="$style.fieldGroup"
    >
      <GuideMessage
        caretPosition="left"
        :class="$style.assignmentGuideMessage"
        name="applicationAssignment"
      />
      <SegmentedButton
        :class="$style.fieldItem"
        :data-test-id="$testId('appCategoryNavigation')"
        name="applications"
        :options="[$options.appButtonOptions.all, $options.appButtonOptions.some]"
        :showValidationMessage="false"
        :value="applicationToggle"
        @input="handleAllApplicationChange"
      />
      <div :class="$style.applicationMultiSelect">
        <ApplicationMultiSelect
          v-if="!includeAllApplications"
          :data-test-id="$testId('applicationMultiSelect')"
          :value="selectedApplications"
          @input="value => handleAppsChange(value)"
        />
      </div>
    </div>

    <div :class="$style.usersSection">
      <HeadingExtraSmall element="h3">
        Users
      </HeadingExtraSmall>
      <ParagraphBody>
        If a user is both included and excluded, the policy won't apply to the user.
      </ParagraphBody>
      <div :class="$style.fieldGroup">
        <div :class="$style.includedUsersSection">
          <UserGroupSelection
            :appInfoMessage="userAppInfoMessage"
            :data-test-id="$testId('includedUserGroups')"
            :hideSearch="includeAllUsers"
            :knowledgeBaseDetails="knowledgeBaseDetails"
            :previousIncludedGroups="previousIncludedUserGroups"
            :searchValue="includedUserGroups"
            :showNotification="showLdapMessage"
            :tabButtonOptions="userButtonOptions"
            :tabButtonValue="userButtonValue"
            title="Included Users"
            :validationConfig="validationConfig.userGroups.included"
            @handleCategoryChange="handleUserCategoryChange"
            @handleGroupsChange="value => handleUserGroupsChange(value, 'inclusions')"
          />
        </div>

        <div :class="$style.excludedUsersSection">
          <UserGroupSelection
            :data-test-id="$testId('excludedUserGroups')"
            :searchValue="excludedUserGroups"
            :showGuideMessage="true"
            title="Excluded User Groups"
            :validationConfig="validationConfig.userGroups.excluded"
            @handleGroupsChange="value => handleUserGroupsChange(value, 'exclusions')"
          />

          <div
            v-if="isLdap"
            :class="$style.ldapMessage"
          >
            <Checkbox
              label="(Recommended) Exclude LDAP Bind DN Users"
              :value="isLdapBindUserExcluded"
              @input="handleLdapBindUserChange"
            />
            <TooltipIcon
              :class="$style.tooltipIcon"
              icon="info"
              orientation="right"
              text="JumpCloud recommends excluding LDAP Bind DN users because MFA
            may prevent the Bind DN Account from authenticating."
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';

import AuthNPolicyType, { authnPolicyType } from 'client/Security/AuthNPolicyType';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';

import {
  Checkbox,
  HeadingExtraSmall,
  Icon,
  ParagraphBody,
  SegmentedButton,
  TooltipIcon,
} from '@jumpcloud/ui-components';
import ApplicationMultiSelect from 'client/components/Applications/ApplicationMultiSelect.vue';
import GuideMessage from 'client/components/GuideMessage/GuideMessage.vue';
import UserGroupSelection from 'client/Security/AuthNPoliciesAsidePanel/UserGroupSelection.vue';

const appButtonOptions = {
  all: { label: 'All Applications', value: 'all' },
  some: { label: 'Select Applications', value: 'app' },
};

const assignmentDataByResourceType = {
  [authnPolicyType.userportal]: {
    icon: 'userGroup',
    text: 'User Portal',
  },
  [authnPolicyType.application]: {
    icon: 'sso',
    text: 'SSO Applications',
    kb: {
      url: KbArticleUrls.ssoApplicationsUserAuth,
      text: 'Learn More: Authorize Users to an SSO Application',
    },
  },
  [authnPolicyType.ldap]: {
    icon: 'ldap',
    text: 'JumpCloud LDAP',
    kb: {
      url: KbArticleUrls.ldapCloudService,
      text: 'Learn More: Using JumpCloud’s LDAP-as-a-Service',
    },
  },
};

export default {
  name: 'AssignmentsSection',

  components: {
    ApplicationMultiSelect,
    Checkbox,
    HeadingExtraSmall,
    Icon,
    GuideMessage,
    ParagraphBody,
    SegmentedButton,
    TooltipIcon,
    UserGroupSelection,
  },

  props: {
    previousValue: {
      type: AuthNPolicyType,
      default: new AuthNPolicyType(),
    },
    value: {
      type: AuthNPolicyType,
      default: new AuthNPolicyType(),
    },
  },

  data() {
    return {
      applicationToggle: get(this.value.targets, 'applications', []).length
        ? appButtonOptions.some.value
        : appButtonOptions.all.value,
    };
  },

  appButtonOptions,

  computed: {
    assignmentData() {
      return get(assignmentDataByResourceType, this.value.type, {});
    },

    excludedUserGroups() {
      return get(this.value.targets, 'userGroups.exclusions', []);
    },

    includeAllApplications() {
      return this.applicationToggle === 'all';
    },

    includeAllUsers() {
      return this.value.targets.users.inclusions.includes('all');
    },

    includedUserGroups() {
      return get(this.value.targets, 'userGroups.inclusions', []);
    },

    isLdap() {
      return this.value.type === authnPolicyType.ldap;
    },

    isLdapBindUserExcluded() {
      const exclusions = get(this.value.targets, 'userAttributes.exclusions', []);
      return !!exclusions.find(data => data.field === 'ldap_binding_user');
    },

    isSSO() {
      return this.value.type === authnPolicyType.application;
    },

    knowledgeBaseDetails() {
      return this.assignmentData.kb || {};
    },

    previousIncludedUserGroups() {
      return get(this.previousValue.targets, 'userGroups.inclusions', []);
    },

    selectedApplications() {
      return get(this.value.targets, 'applications', []);
    },

    showLdapMessage() {
      return !this.includeAllUsers && (this.isSSO || this.isLdap);
    },

    userAppInfoMessage() {
      return this.isSSO
        ? "Access Policies don't assign users to applications and won't apply if the users aren't assigned to the selected applications."
        : "Access Policies don't assign users to JumpCloud LDAP and won't apply if the users aren't assigned to JumpCloud LDAP.";
    },

    userButtonOptions() {
      return [
        { label: 'All Users', value: 'all' },
        { label: 'Select User Groups', value: 'none' },
      ];
    },

    userButtonValue() {
      const [all, none] = this.userButtonOptions;
      return this.includeAllUsers ? all.value : none.value;
    },

    validationConfig() {
      const configuration = {
        userGroups: {
          excluded: {
            name: 'excludeUserGroups',
            get rules() {
              const validationRuleName = 'duplicateGroups';
              const { included } = configuration.userGroups;
              return `${validationRuleName}:@${included.name}`;
            },
            disabled: false,
          },
          included: {
            name: 'includeUserGroups',
            rules: 'userAssignmentIsRequired',
            disabled: false,
          },
        },
      };

      return configuration;
    },
  },

  methods: {
    handleAllApplicationChange(value) {
      this.applicationToggle = value;
      if (value === 'all') {
        this.handleAppsChange([]);
      }
    },

    handleAppsChange(newValue) {
      this.handleValueChange({
        ...this.value.targets,
        applications: newValue,
      }, 'targets');
    },

    handleLdapBindUserChange(isChecked) {
      const userAttributes = get(this.value.targets, 'userAttributes', {});
      const exclusions = get(userAttributes, 'exclusions', []);

      const updated = isChecked
        ? [...exclusions, { field: 'ldap_binding_user', operator: 'EQ', value: true }]
        : exclusions.filter(exclusion => exclusion.field !== 'ldap_binding_user');

      this.handleTargetsChange({
        ...userAttributes,
        exclusions: updated,
      }, 'userAttributes');
    },

    handleTargetsChange(newValue, key) {
      const { targets } = this.value;
      this.handleValueChange({
        ...targets,
        [key]: newValue,
      }, 'targets');
    },

    handleUserCategoryChange(newValue) {
      const exclusions = this.excludedUserGroups;
      const userGroups = {
        ...(exclusions.length > 0 && {
          exclusions,
        }),
      };

      if (newValue === this.userButtonOptions[0].value) {
        // Clear included userGroups and Include all users
        this.handleValueChange({
          ...this.value.targets,
          users: {
            inclusions: ['all'],
          },
          userGroups,
        }, 'targets');
      } else {
        // Don't include all users
        this.handleUsersChange([], 'inclusions');
      }
    },

    handleUserGroupsChange(newValue, key) {
      const targets = this.value.targets || {};
      const { userGroups } = targets;

      this.handleTargetsChange({
        ...userGroups,
        [key]: newValue,
      }, 'userGroups');
    },

    handleUsersChange(newValue, key) {
      const targets = this.value.targets || {};
      const { users } = targets;

      this.handleTargetsChange({
        ...users,
        [key]: newValue,
      }, 'users');
    },

    handleValueChange(newValue, key) {
      this.$emit('valueChange', {
        ...this.value,
        [key]: newValue,
      });
    },
  },
};
</script>

<style module>
  .applicationMultiSelect,
  .assignmentIconTitle,
  .excludedUsersSection,
  .includedUsersSection {
    position: relative;
  }

  .assignmentResource,
  .ldapMessage {
    align-items: center;
    display: flex;
    margin-top: var(--jc-spacer-small);
  }

  .applicationMultiSelect,
  .fieldItem,
  .fieldGroup {
    margin-top: var(--jc-spacer);
  }

  .excludedUsersSection,
  .usersSection {
    margin-top: var(--jc-spacer-medium);
  }

  .fieldGroup {
    padding-left: var(--jc-spacer-large);
  }

  .assignmentIconTitle {
    top: 3px;
  }

  .iconWrapper {
    font-size: var(--jcBodyLarge);
  }

  .assignmentGuideMessage div {
    left: 420px;
    top: -72px;
  }

  .applicationMultiSelect {
    z-index: 4;
  }

  .includedUsersSection {
    z-index: 3;
  }

  .excludedUsersSection {
    z-index: 2;
  }
</style>
